"use client";

import React from "react";
import Nav from "./Nav";
import Link from "next/link";
import Image from "next/image";
import { useRouter, usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { Search, Camera } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Avatar from "@/components/common/Avatar";
import { tenureLabel } from "@/utlis/tenureLabel";

// Utils
import { userService } from "@/services/user.service";
import { fetchWrapper } from "@/helpers/fetch-wrapper";

// Context
import { useContextElement } from "@/context/Context";

// Lang Switcher
import RegionSwitcher from "@/components/RegionSwitcher";

// Translation
import { useT } from "@/context/I18nContext";

// PWA
import { usePWA } from "@/utlis/usePWA";

export default function Header4() {
	const { t } = useT();
	const isPWA = usePWA();

	const [userData, setUserData] = useState([]);
	const [userState, setUserState] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [isCategory, setIsCategory] = useState(false);
	const [isProductPage, setIsProductPage] = useState(false);
	const [bannerText, setBannerText] = useState("");
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [pwaScrolled, setPwaScrolled] = useState(false);
	const [avatarUrl, setAvatarUrl] = useState(null);

	const userName = userData?.firstName ? `${userData.firstName} ${userData.lastName || ""}`.trim() : "Utilizator";

	useEffect(() => {
		setAvatarUrl(userData?.avatar || null);
	}, [userData?.avatar]);

	const handleAvatarChange = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		try {
			const fd = new FormData();
			fd.append("avatar", file);
			const res = await fetchWrapper.post("/api/users/avatar", fd);
			setAvatarUrl(res.url);
			toast.success("Poza de profil a fost actualizată.");
		} catch (error) {
			toast.error(typeof error === "string" ? error : "Eroare la încărcarea pozei.");
		} finally {
			e.target.value = "";
		}
	};

	const { cartProducts, wishList } = useContextElement();

	const router = useRouter();
	const pathname = usePathname();
	const isAccountSection = pathname?.startsWith("/contul-meu");

	const cartCount = cartProducts.reduce((sum, item) => sum + (item.quantity || 0), 0);
	const wishCount = wishList.length;

	useEffect(() => {
		let alive = true;
		const safe = (fn) => alive && fn();

		const fetchUserData = async () => {
			try {
				const u = userService.userValue;
				if (!u?.token) {
					safe(() => {
						setUserData([]);
						setUserState(false);
					});
					return;
				}
				const response = await fetchWrapper.get(`/api/users/getdata`);
				safe(() => {
					setUserData(response?.[0]?.userData ?? []);
					setUserState(true);
				});
			} catch (e) {
				safe(() => {
					setUserData([]);
					setUserState(false);
				});
			}
		};

		const fetchSiteSettings = async () => {
			try {
				const response = await fetch(`/api/site-settings`, { method: "GET", cache: "no-store" });
				if (!response.ok) return;
				const data = await response.json();
				if (data?.bannerText?.trim()) setBannerText(data.bannerText);
			} catch (e) {
				console.error(e);
			}
		};

		fetchUserData();
		fetchSiteSettings();

		const sub = userService.user.subscribe(() => {
			fetchUserData();
			fetchSiteSettings();
		});

		return () => {
			alive = false;
			sub.unsubscribe();
		};
	}, []);

	useEffect(() => {
		const el = document.getElementById("mobileMenu");
		if (!el) return;
		const onShow = () => {
			setMobileMenuOpen(true);
			document.body.classList.add("mobile-menu-open");
		};
		const onHide = () => {
			setMobileMenuOpen(false);
			document.body.classList.remove("mobile-menu-open");
		};
		el.addEventListener("show.bs.offcanvas", onShow);
		el.addEventListener("hide.bs.offcanvas", onHide);
		return () => {
			el.removeEventListener("show.bs.offcanvas", onShow);
			el.removeEventListener("hide.bs.offcanvas", onHide);
		};
	}, []);

	useEffect(() => {
		let cancelled = false;

		async function checkIfCategory() {
			if (!pathname || pathname === "/") {
				setIsCategory(false);
				setIsProductPage(false);
				return;
			}
			const slugPath = pathname.replace(/^\//, "");
			if (!slugPath) {
				setIsCategory(false);
				setIsProductPage(false);
				return;
			}
			try {
				const response = await fetch(`/api/categories/${slugPath}`);
				const isCat = response.ok && (await response.clone().json())?.productsData ? true : false;
				if (cancelled) return;
				setIsCategory(isCat);

				if (!isCat && !slugPath.includes("/")) {
					try {
						const pRes = await fetch(`/api/products/${slugPath}`);
						if (!cancelled) setIsProductPage(pRes.ok);
					} catch {
						if (!cancelled) setIsProductPage(false);
					}
				} else if (!cancelled) {
					setIsProductPage(false);
				}
			} catch (error) {
				if (!cancelled) {
					setIsCategory(false);
					setIsProductPage(false);
				}
			}
		}
		checkIfCategory();

		return () => {
			cancelled = true;
		};
	}, [pathname]);

	const handleSearch = (e) => {
		e.preventDefault();
		if (!searchQuery.trim()) return;
		const sanitizedQuery = searchQuery.trim().slice(0, 100);
		if (pathname === "/" || pathname === "" || !isCategory) {
			router.push(`/?q=${encodeURIComponent(sanitizedQuery)}`);
		} else {
			router.push(`${pathname}?q=${encodeURIComponent(sanitizedQuery)}`);
		}
	};

	useEffect(() => {
		if (typeof window !== "undefined") {
			const params = new URLSearchParams(window.location.search);
			const currentQuery = params.get("q");
			if (currentQuery) setSearchQuery(currentQuery);
			else setSearchQuery("");
		}
	}, [pathname]);

	const copyLegacy = (text) => {
		// navigator.clipboard cere context securizat (https/localhost) - pe http
		// (ex: IP local fara tunel) nu exista deloc, de-aici fallback-ul vechi
		const input = document.createElement("textarea");
		input.value = text;
		input.style.position = "fixed";
		input.style.opacity = "0";
		document.body.appendChild(input);
		input.focus();
		input.select();
		let ok = false;
		try {
			ok = document.execCommand("copy");
		} catch {
			ok = false;
		}
		document.body.removeChild(input);
		return ok;
	};

	const copyLink = async (url) => {
		if (navigator.clipboard) {
			try {
				await navigator.clipboard.writeText(url);
				toast.success("Link copiat!");
				return;
			} catch {
				// cade pe fallback-ul de mai jos
			}
		}
		if (copyLegacy(url)) {
			toast.success("Link copiat!");
		} else {
			toast.error("Nu am putut copia linkul.");
		}
	};

	const handleShare = async () => {
		if (typeof window === "undefined") return;
		const url = window.location.href;

		if (navigator.share) {
			try {
				await navigator.share({ url });
				return;
			} catch (err) {
				if (err?.name === "AbortError") return;
				// share a esuat (ex: lipsa context securizat) - incearca copiere
			}
		}
		await copyLink(url);
	};

	useEffect(() => {
		if (!isPWA) return;
		const onScroll = () => setPwaScrolled(window.scrollY > 6);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, [isPWA]);

	// ─── PWA Header ────────────────────────────────────────────────────────────
	if (isPWA) {
		return (
			<>
				<style jsx>{`
					.pwa-header {
						position: sticky;
						top: 0;
						z-index: 1000;
						padding: env(safe-area-inset-top) 0 0;
						transition:
							background 0.2s ease,
							box-shadow 0.2s ease,
							border-color 0.2s ease;
					}

					.pwa-header-inner {
						display: flex;
						align-items: center;
						gap: 8px;
						padding: 10px 14px;
					}

					.pwa-search-form {
						flex: 1;
						min-width: 0;
						display: flex;
						align-items: center;
						gap: 8px;
						background: #ffffff;
						border-radius: 999px;
						padding: 0 14px;
						height: 44px;
						border: 1.5px solid transparent;
						box-shadow:
							0 10px 30px rgba(15, 23, 42, 0.14),
							0 2px 8px rgba(15, 23, 42, 0.06);
						transition:
							border-color 0.2s,
							box-shadow 0.2s;
					}

					.pwa-search-form:focus-within {
						border-color: var(--primary, #0d6efd);
						box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
					}

					.pwa-search-icon {
						flex-shrink: 0;
						display: flex;
						align-items: center;
						color: #6b7280;
					}

					.pwa-search-input {
						flex: 1;
						min-width: 0;
						border: none;
						background: transparent;
						padding: 0;
						font-size: 15px;
						color: #111;
						outline: none;
					}

					.pwa-search-input::placeholder {
						color: #9ca3af;
					}

					.pwa-region-btn {
						flex-shrink: 0;
						width: 44px;
						height: 44px;
						border-radius: 50%;
						background: #ffffff;
						box-shadow:
							0 10px 30px rgba(15, 23, 42, 0.14),
							0 2px 8px rgba(15, 23, 42, 0.06);
						display: flex;
						align-items: center;
						justify-content: center;
						transition: box-shadow 0.15s;
					}

					.pwa-region-btn:active {
						box-shadow:
							0 4px 14px rgba(15, 23, 42, 0.16),
							0 1px 4px rgba(15, 23, 42, 0.08);
					}

					.pwa-icon-btn {
						flex-shrink: 0;
						width: 44px;
						height: 44px;
						border: 0;
						border-radius: 50%;
						background: #ffffff;
						color: #111;
						box-shadow:
							0 10px 30px rgba(15, 23, 42, 0.14),
							0 2px 8px rgba(15, 23, 42, 0.06);
						display: flex;
						align-items: center;
						justify-content: center;
						transition: box-shadow 0.15s;
					}

					.pwa-icon-btn:active {
						box-shadow:
							0 4px 14px rgba(15, 23, 42, 0.16),
							0 1px 4px rgba(15, 23, 42, 0.08);
					}

					.pwa-product-title {
						flex: 1;
						min-width: 0;
						font-size: 15px;
						font-weight: 600;
						color: #111;
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
					}

					.pwa-account-bar {
						display: flex;
						align-items: center;
						gap: 14px;
						width: 100%;
					}
					.pwa-account-avatar-wrap {
						position: relative;
						flex-shrink: 0;
					}
					.pwa-account-avatar-wrap :global(.tf-avatar) {
						border: 2px solid rgba(0, 0, 0, 0.08);
					}
					.pwa-account-avatar-edit {
						position: absolute;
						bottom: -2px;
						right: -2px;
						width: 20px;
						height: 20px;
						border-radius: 50%;
						background: #fff;
						color: #16151a;
						display: flex;
						align-items: center;
						justify-content: center;
						cursor: pointer;
						box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
					}
					.pwa-account-name {
						color: #111;
						font-weight: 700;
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
						transition: font-size 0.15s ease;
					}
					.pwa-account-sub {
						color: #6b6b76;
						font-size: 12px;
						margin-top: 2px;
					}
				`}</style>

				<header
					className='pwa-header'
					style={{
						background: !pwaScrolled || isAccountSection ? "rgba(255, 255, 255, 0.97)" : "transparent",
						backdropFilter: !pwaScrolled || isAccountSection ? "blur(20px) saturate(180%)" : "none",
						WebkitBackdropFilter: !pwaScrolled || isAccountSection ? "blur(20px) saturate(180%)" : "none",
						borderBottom: !pwaScrolled || isAccountSection ? "1px solid rgba(0, 0, 0, 0.07)" : "1px solid transparent",
						boxShadow: !pwaScrolled || isAccountSection ? "0 1px 12px rgba(0, 0, 0, 0.06)" : "none",
					}}>
					<div className='pwa-header-inner' style={isAccountSection ? { padding: pwaScrolled ? "8px 14px" : "14px 14px 18px" } : undefined}>
						{isAccountSection ? (
							<div className='pwa-account-bar'>
								<div className='pwa-account-avatar-wrap'>
									<Avatar url={avatarUrl} name={userName} size={pwaScrolled ? 34 : 52} />
									{!pwaScrolled && (
										<label className='pwa-account-avatar-edit' aria-label='Schimbă poza de profil'>
											<Camera size={11} />
											<input
												type='file'
												accept='image/png,image/jpeg,image/webp'
												hidden
												onChange={handleAvatarChange}
											/>
										</label>
									)}
								</div>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div className='pwa-account-name' style={{ fontSize: pwaScrolled ? 15 : 18 }}>
										{userName}
									</div>
									{!pwaScrolled && userData?.createdAt && (
										<div className='pwa-account-sub'>{tenureLabel(userData.createdAt)}</div>
									)}
								</div>
							</div>
						) : isProductPage ? (
							<>
								<button
									type='button'
									className='pwa-icon-btn'
									aria-label='Înapoi'
									onClick={() => router.back()}>
									<svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
										<path d='M15 18l-6-6 6-6' />
									</svg>
								</button>

								<div style={{ flex: 1 }} />

								<button
									type='button'
									className='pwa-icon-btn'
									aria-label='Distribuire'
									onClick={handleShare}>
									<svg width='19' height='19' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.1' strokeLinecap='round' strokeLinejoin='round'>
										<path d='M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7' />
										<polyline points='16 6 12 2 8 6' />
										<line x1='12' y1='2' x2='12' y2='15' />
									</svg>
								</button>
							</>
						) : (
							<>
								<form className='pwa-search-form' onSubmit={handleSearch}>
									<span className='pwa-search-icon'>
										<Search size={18} strokeWidth={2.2} />
									</span>
									<input
										className='pwa-search-input'
										type='text'
										placeholder={t("cauta") || "Începe o nouă căutare"}
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
									/>
								</form>

								<div className='pwa-region-btn'>
									<RegionSwitcher />
								</div>
							</>
						)}
					</div>
				</header>

				{/* Banner ascuns pe PWA — vizibil doar in browser normal */}
			</>
		);
	}

	// ─── Normal (Browser) Header ───────────────────────────────────────────────
	return (
		<>
			{bannerText && !mobileMenuOpen && (
				<div className='info-banner-wrapper'>
					<style jsx>{`
						.info-banner-wrapper {
							background: linear-gradient(135deg, #007bff 0%, #2da4d0ff 100%);
							position: relative;
							overflow: hidden;
						}
						.info-banner-wrapper::before {
							content: "";
							position: absolute;
							top: 0;
							left: -100%;
							width: 100%;
							height: 100%;
							background: linear-gradient(
								90deg,
								transparent,
								rgba(255, 255, 255, 0.1),
								transparent
							);
							animation: shine 3s infinite;
						}
						@keyframes shine {
							0% {
								left: -100%;
							}
							100% {
								left: 100%;
							}
						}
						.info-banner {
							padding: 12px 20px;
							animation: slideDown 0.4s ease-out;
						}
						@keyframes slideDown {
							from {
								transform: translateY(-100%);
								opacity: 0;
							}
							to {
								transform: translateY(0);
								opacity: 1;
							}
						}
						.banner-container {
							max-width: 1400px;
							margin: 0 auto;
							display: flex;
							align-items: center;
							justify-content: center;
							position: relative;
							gap: 12px;
						}
						.banner-icon {
							background: rgba(255, 255, 255, 0.2);
							width: 32px;
							height: 32px;
							border-radius: 50%;
							display: flex;
							align-items: center;
							justify-content: center;
							flex-shrink: 0;
							backdrop-filter: blur(10px);
							box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
						}
						.banner-icon svg {
							width: 18px;
							height: 18px;
							color: white;
						}
						.banner-content {
							display: flex;
							align-items: center;
							gap: 8px;
						}
						.banner-text {
							color: white;
							font-size: 14px;
							font-weight: 500;
							letter-spacing: 0.3px;
							text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
							margin: 0;
							line-height: 1.4;
						}
						@media (max-width: 768px) {
							.info-banner {
								padding: 10px 15px;
							}
							.banner-text {
								font-size: 13px;
							}
							.banner-icon {
								width: 28px;
								height: 28px;
							}
							.banner-icon svg {
								width: 16px;
								height: 16px;
							}
						}
						@media (max-width: 480px) {
							.banner-container {
								gap: 8px;
							}
							.banner-text {
								font-size: 12px;
							}
						}
					`}</style>
					<div className='info-banner'>
						<div className='banner-container'>
							<div className='banner-icon'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									viewBox='0 0 24 24'
									fill='none'
									stroke='currentColor'
									strokeWidth='2.5'
									strokeLinecap='round'
									strokeLinejoin='round'>
									<circle cx='12' cy='12' r='10' />
									<line x1='12' y1='16' x2='12' y2='12' />
									<line x1='12' y1='8' x2='12.01' y2='8' />
								</svg>
							</div>
							<div className='banner-content'>
								<p className='banner-text'>{bannerText}</p>
							</div>
						</div>
					</div>
				</div>
			)}

			<header id='header' className='header-default header-style-2'>
				<style jsx>{`
					.tf-search-mobile {
						padding: 5px 0 1px;
						background: transparent;
					}
					.tf-search-mobile form {
						display: flex;
						align-items: center;
						gap: 0;
						height: 44px;
						border-radius: 9999px;
						border: 1px solid #dcdcdc;
						background-color: #f7f7f7;
						padding: 0 6px 0 16px;
						transition: all 0.2s ease-in-out;
					}
					.tf-search-mobile form:focus-within {
						background-color: #fff;
						border-color: #0074d9;
						box-shadow: 0 0 0 2px rgba(0, 116, 217, 0.15);
					}
					.tf-search-mobile input {
						flex: 1;
						height: 100%;
						border: none;
						background: transparent;
						padding: 0 8px 0 0;
						font-size: 16px;
						color: #262626;
					}
					.tf-search-mobile input::placeholder {
						color: #5c5c5c;
					}
					.tf-search-mobile input:focus {
						outline: none;
					}
					.tf-search-mobile button {
						width: 34px;
						height: 34px;
						flex-shrink: 0;
						border: none;
						border-radius: 50%;
						background-color: transparent;
						display: flex;
						align-items: center;
						justify-content: center;
						color: #555;
						transition: background-color 0.2s ease-in-out;
					}
					.tf-search-mobile button:hover {
						background-color: rgba(0, 0, 0, 0.06);
					}
					.cart-badge-wrapper {
						position: relative;
						display: inline-block;
					}
					.cart-badge {
						position: absolute;
						top: -8px;
						right: -8px;
						background-color: #ff4444;
						color: white;
						border-radius: 50%;
						min-width: 20px;
						height: 20px;
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 11px;
						font-weight: 600;
						padding: 0 5px;
						box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
						z-index: 1;
					}
					@media (max-width: 992px) {
						.tf-search-mobile {
							margin-bottom: 16px;
						}
						.wrapper-header {
							min-height: 52px;
						}
					}
				`}</style>
				<div className='main-header line'>
					<div className={`container-full px_15 lg-px_40 ${isProductPage ? "tf-product-page-container" : ""}`}>
						<div className='row wrapper-header align-items-center'>
							<div className='col-xl-2 tf-md-hidden'>
								<div className='tf-cur'>
									<Link href={`/`} className='logo-header' style={{ display: "inline-flex", lineHeight: 0 }}>
										<Image
											src='/images/logo/emagyl.webp'
											alt='eMagyl'
											width={200}
											height={200}
											priority
											style={{ height: "42px", width: "42px" }}
										/>
									</Link>
								</div>
							</div>

							<div className='col-8 tf-lg-hidden'>
								<div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
									<a href='#mobileMenu' data-bs-toggle='offcanvas' aria-controls='mobileMenu' aria-label='Meniu'>
										{mobileMenuOpen ? (
											<svg
												xmlns='http://www.w3.org/2000/svg'
												width={20}
												height={20}
												viewBox='0 0 20 20'
												fill='none'>
												<path
													d='M15 5L5 15M5 5L15 15'
													stroke='currentColor'
													strokeWidth='2'
													strokeLinecap='round'
												/>
											</svg>
										) : (
											<svg
												xmlns='http://www.w3.org/2000/svg'
												width={24}
												height={16}
												viewBox='0 0 24 16'
												fill='none'>
												<path
													d='M2.00056 2.28571H16.8577C17.1608 2.28571 17.4515 2.16531 17.6658 1.95098C17.8802 1.73665 18.0006 1.44596 18.0006 1.14286C18.0006 0.839753 17.8802 0.549063 17.6658 0.334735C17.4515 0.120408 17.1608 0 16.8577 0H2.00056C1.69745 0 1.40676 0.120408 1.19244 0.334735C0.978109 0.549063 0.857702 0.839753 0.857702 1.14286C0.857702 1.44596 0.978109 1.73665 1.19244 1.95098C1.40676 2.16531 1.69745 2.28571 2.00056 2.28571ZM0.857702 8C0.857702 7.6969 0.978109 7.40621 1.19244 7.19188C1.40676 6.97755 1.69745 6.85714 2.00056 6.85714H22.572C22.8751 6.85714 23.1658 6.97755 23.3801 7.19188C23.5944 7.40621 23.7148 7.6969 23.7148 8C23.7148 8.30311 23.5944 8.59379 23.3801 8.80812C23.1658 9.02245 22.8751 9.14286 22.572 9.14286H2.00056C1.69745 9.14286 1.40676 9.02245 1.19244 8.80812C0.978109 8.59379 0.857702 8.30311 0.857702 8ZM0.857702 14.8571C0.857702 14.554 0.978109 14.2633 1.19244 14.049C1.40676 13.8347 1.69745 13.7143 2.00056 13.7143H12.2863C12.5894 13.7143 12.8801 13.8347 13.0944 14.049C13.3087 14.2633 13.4291 14.554 13.4291 14.8571C13.4291 15.1602 13.3087 15.4509 13.0944 15.6653C12.8801 15.8796 12.5894 16 12.2863 16H2.00056C1.69745 16 1.40676 15.8796 1.19244 15.6653C0.978109 15.4509 0.857702 15.1602 0.857702 14.8571Z'
													fill='currentColor'
												/>
											</svg>
										)}
									</a>
									<Link href={`/`} className='logo-header only-mobile' style={{ display: "inline-flex", lineHeight: 0 }}>
										<Image
											src='/images/logo/emagyl.webp'
											alt='eMagyl'
											width={200}
											height={200}
											priority
											style={{ height: "36px", width: "36px" }}
										/>
									</Link>
								</div>
							</div>

							<div className='col-xl-6 tf-md-hidden text-center'>
								<div className='tf-search-sticky nav-icon-text' style={{ width: "100%" }}>
									<form
										className='tf-mini-search-frm'
										onSubmit={handleSearch}
										style={{ display: "flex", alignItems: "center" }}>
										<fieldset className='tf-mini-search-frm' style={{ flexGrow: 1 }}>
											<input
												type='text'
												placeholder={t("cauta")}
												tabIndex='0'
												aria-required='true'
												required
												value={searchQuery}
												onChange={(e) => setSearchQuery(e.target.value)}
												style={{
													minHeight: "44px",
													width: "100%",
													padding: "10px 12px",
													fontSize: "16px",
												}}
											/>
										</fieldset>
									</form>
								</div>
							</div>

							<div className='col-xl-4 col-4'>
								<ul
									className='nav-icon d-flex justify-content-end align-items-center'
									style={{ gap: "14px", flexWrap: "nowrap" }}>
									<li className='nav-cart tf-md-hidden'>
										<a
											href={userState ? "/contul-meu" : "/autentificare"}
											className='nav-icon-item'
											aria-label={userState && userData ? userData.firstName + " " + userData.lastName : t("contul_meu")}>
											<i className='icon icon-account' style={{ display: "inline-block", width: "1em", textAlign: "center" }} />{" "}
											<div className='nav-icon-text' style={{ marginLeft: 5, whiteSpace: "nowrap", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis" }}>
												{userState && userData ? (
													<>{userData.firstName + " " + userData.lastName}</>
												) : (
													<>{t("contul_meu")}</>
												)}
											</div>
										</a>
									</li>

									<li className='nav-cart'>
										<Link href={`/wishlist`} className='nav-icon-item' aria-label={t("favorite")}>
											<div className='cart-badge-wrapper'>
												<i className='icon icon-heart' style={{ fontSize: "20px", display: "inline-block", width: "1em", textAlign: "center" }} />
												<span className='cart-badge' style={{ visibility: wishCount > 0 ? "visible" : "hidden" }} aria-hidden='true'>{wishCount}</span>
											</div>
											<div
												className='nav-icon-text tf-md-hidden'
												style={{ marginLeft: 5, whiteSpace: "nowrap" }}>
												{t("favorite")}
											</div>
										</Link>
									</li>

									<li className='nav-cart'>
										<Link href='/cos-de-cumparaturi' className='nav-icon-item' aria-label={t("cosul_meu")}>
											<div className='cart-badge-wrapper'>
												<i className='icon icon-bag' style={{ fontSize: "20px", display: "inline-block", width: "1em", textAlign: "center" }} />
												<span className='cart-badge' style={{ visibility: cartCount > 0 ? "visible" : "hidden" }} aria-hidden='true'>{cartCount}</span>
											</div>
											<div
												className='nav-icon-text tf-md-hidden'
												style={{ marginLeft: 5, whiteSpace: "nowrap" }}>
												{t("cosul_meu")}
											</div>
										</Link>
									</li>

									<li
										className='nav-cart tf-md-hidden'
										style={{ display: "flex", alignItems: "center", gap: 8 }}>
										<RegionSwitcher />
									</li>
								</ul>
							</div>
						</div>

						<div className='row tf-lg-hidden' style={{ display: mobileMenuOpen ? "none" : undefined }}>
							<div className='col-12'>
								<div className='tf-search-mobile'>
									<form onSubmit={handleSearch}>
										<input
											type='text'
											placeholder={t("cauta_extins")}
											required
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
										/>
										<button type='submit' aria-label={t("cauta")}>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												width='18'
												height='18'
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'>
												<circle cx='11' cy='11' r='8' />
												<path d='m21 21-4.35-4.35' />
											</svg>
										</button>
									</form>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className='header-bottom line tf-md-hidden'>
					<div className={`container-full px_15 lg-px_40 ${isProductPage ? "tf-product-page-container" : ""}`}>
						<div className='wrapper-header d-flex justify-content-start align-items-start'>
							<nav className='box-navigation text-center'>
								<ul className='box-nav-ul d-flex align-items-center justify-content-center gap-30'>
									<Nav isAdmin={userState && userData?.admin >= 1} />
								</ul>
							</nav>
						</div>
					</div>
				</div>
			</header>

		</>
	);
}
